import { useMemo, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import { useAppSelector } from '../../../app/store';
import {
  selectFilters,
  selectCategoryMappingIsReady,
  selectIdToSlugMap,
  idsToSlugs,
} from '../selectors';
import { stringifyUrlParams } from '../../../utils/urlParams';

interface DerivedSearchParamsResult {
  derivedSearchParams: string;
  origin: string | null;
}

export default function useDerivedSearchParams(): DerivedSearchParamsResult {
  const filters = useAppSelector(selectFilters);
  const isReady = useAppSelector(selectCategoryMappingIsReady);
  const idToSlugMap = useAppSelector(selectIdToSlugMap);
  const location = useLocation();
  const originRef = useRef<string | null>(null);

  if (originRef.current === null) {
    const searchParams = new URLSearchParams(location.search);
    originRef.current = searchParams.get('origin');
  }

  const origin = originRef.current;
  const derivedSearchParams = useMemo(() => {
    const { categories, ...restFilters } = filters;
    const categoryNames =
      isReady && categories?.length
        ? idsToSlugs(idToSlugMap, categories)
        : undefined;

    return stringifyUrlParams({
      ...restFilters,
      categories: categoryNames,
      ...(origin && { origin }),
    });
  }, [filters, idToSlugMap, isReady, origin]);

  return { derivedSearchParams, origin };
}
